import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Sparkle, Compass, MagnifyingGlass, ChatCircleText,
  Buildings, MapPin, Envelope, Phone, InstagramLogo, LinkedinLogo,
  Globe, WhatsappLogo, Robot, CaretDown, CaretRight, Lightbulb,
  ClockCounterClockwise, ArrowUpRight,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

// ---------------------------------------------------------------------------
// Dashboard = the Daily AI Briefing.
//
// New responsibility (Feb-2026): the Dashboard is NOT a search screen.
// Manual discovery lives at /arama. This page shows what the autonomous
// AI Employee has been working on for the user — today's opportunities,
// strategic recommendations from the last briefing, and quick links.
// ---------------------------------------------------------------------------
export default function Dashboard() {
  const { user } = useAuth();
  const { locale } = useI18n();
  const nav = useNavigate();
  const t = (tr, en) => (locale === "en" ? en : tr);

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto space-y-8" data-testid="dashboard-root">
      {/* Header + slogan */}
      <header>
        <div className="acas-eyebrow mb-2">{t("Günlük AI Brifingi", "Daily AI Briefing")}</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="dash-title">
          {user?.name
            ? t(`Günaydın, ${user.name.split(" ")[0]}.`, `Good morning, ${user.name.split(" ")[0]}.`)
            : t("Günaydın.", "Good morning.")}
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed" data-testid="dash-slogan">
          {t("Yapay Zekâ İş Geliştirme Çalışanı", "AI Business Development Employee")}
        </p>
      </header>

      {/* Daily briefing card */}
      <DailyBriefingCard locale={locale} t={t} nav={nav} />

      {/* Today's Next Best Actions — the Conversion Engine V1 queue */}
      <TodaysNextActionsCard locale={locale} t={t} nav={nav} />

      {/* Today's Opportunities (collapsible) */}
      <TodaysOpportunitiesCard locale={locale} t={t} nav={nav} />

      {/* Strategic recommendations from the latest AI-Insights briefing */}
      <StrategicSnapshotCard locale={locale} t={t} nav={nav} />

      {/* Quick actions */}
      <QuickActionsRow t={t} nav={nav} />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Today's Next Best Actions — Conversion Engine V1 queue.
// Reads GET /conversion/next-actions. Shows top 3-5 items. Honest empty state.
// ---------------------------------------------------------------------------
function TodaysNextActionsCard({ locale, t, nav }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/conversion/next-actions")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, []);

  if (items === null) {
    return <div className="rounded-lg border border-border/60 bg-card p-5 h-[100px] animate-pulse" />;
  }
  return (
    <div className="rounded-lg border border-accent/30 bg-accent/[0.03] p-5" data-testid="todays-next-actions">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-accent/10 text-accent grid place-items-center">
            <ArrowUpRight size={15} weight="bold" />
          </div>
          <div>
            <div className="text-sm font-bold tracking-tight">{t("Bugün Yapılacaklar", "Today's next best actions")}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5">
              {t("Fırsatlarınızı satışa yaklaştıran adımlar", "Steps that move opportunities closer to a sale")}
            </div>
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/50 p-4 text-center text-xs text-muted-foreground leading-relaxed">
          {t(
            "Şu an dikkat gerektiren bir fırsat yok. Yeni fırsatlar için bir strateji üretin ya da bir arama başlatın.",
            "No opportunity currently needs your attention. Generate a strategy on a lead or start a new search."
          )}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="next-actions-list">
          {items.map((it) => (
            <li key={it.lead_id}>
              <button
                data-testid={`next-action-${it.lead_id}`}
                onClick={() => nav(`/musteriler/${it.lead_id}`)}
                className="w-full text-left rounded-md border border-border/60 bg-card p-3 hover:border-accent/40 hover:bg-accent/[0.03] transition flex items-start gap-3"
              >
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                  it.priority === "high" ? "bg-accent"
                  : it.priority === "medium" ? "bg-amber-500"
                  : "bg-muted-foreground/50"
                }`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-sm truncate">{it.label}</div>
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-border/60 bg-secondary text-muted-foreground font-semibold">
                      {it.company_name || it.lead_id.slice(0, 8)}
                    </span>
                  </div>
                  {it.reason && (
                    <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{it.reason}</div>
                  )}
                </div>
                <ArrowUpRight size={13} className="text-muted-foreground flex-shrink-0 mt-0.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Daily Briefing — "AI has already been working" card. Reads /briefing.
// ---------------------------------------------------------------------------
function DailyBriefingCard({ locale, t, nav }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    api.get("/briefing").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="rounded-lg border border-border/60 bg-card p-5 h-[120px] animate-pulse" />;
  }
  if (!data) return null;

  const enableAutomaticScanning = async () => {
    const brief = data.standing_brief;
    if (!brief) return;
    setEnabling(true);
    try {
      await api.put("/standing-brief", { ...brief, auto_run_enabled: true });
      setData((current) => ({ ...current, auto_run_enabled: true, standing_brief: { ...current.standing_brief, auto_run_enabled: true } }));
    } finally {
      setEnabling(false);
    }
  };

  if (!data.has_standing_brief) {
    return (
      <div className="rounded-lg border-2 border-dashed border-primary/40 bg-primary/[0.03] p-5" data-testid="briefing-onboarding">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 text-primary grid place-items-center flex-shrink-0">
            <Robot size={20} weight="fill" />
          </div>
          <div className="flex-1">
            <div className="acas-eyebrow text-primary/80">{t("AI Ekip Arkadaşınız Bekliyor", "Your AI teammate is ready")}</div>
            <h2 className="text-lg font-bold mt-1">
              {t(
                "Aradığınız fırsat türünü bir kez tarif edin — ben günde bir kez sizin için pazarı tararım.",
                "Tell Next io what kind of opportunities you're after once, and I'll monitor the market for you daily."
              )}
            </h2>
            <a href="/isletme" data-testid="brief-cta" className="inline-block mt-3 text-sm font-semibold text-primary hover:underline">
              {t("Kayıtlı Brief Oluştur →", "Create Standing Brief →")}
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!data.auto_run_enabled) {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/[0.04] p-5" data-testid="briefing-automatic-off">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 text-primary grid place-items-center flex-shrink-0">
            <Robot size={20} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="acas-eyebrow text-primary/80">{t("Pazar Brifingi", "Market Brief")}</div>
            <h2 className="text-lg font-bold mt-1" data-testid="briefing-automatic-off-message">
              {t("Otomatik fırsat taraması kapalı. Bu nedenle son 24 saatte yeni fırsat aranmadı.", "Automatic opportunity scanning is off, so no new opportunities were searched in the last 24 hours.")}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button type="button" size="sm" onClick={enableAutomaticScanning} disabled={enabling} data-testid="briefing-enable-automatic-scan">
                {enabling ? t("Açılıyor…", "Enabling…") : t("Otomatik taramayı aç", "Enable automatic scanning")}
              </Button>
              {data.daily_run_time && (
                <span className="text-xs text-muted-foreground" data-testid="briefing-next-daily-run">
                  {t(`Her gün saat ${data.daily_run_time}’de çalışır.`, `Runs daily at ${data.daily_run_time}.`)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totals = data.totals || {};
  const lastRun = data.last_autonomous_run_at ? new Date(data.last_autonomous_run_at) : null;
  const lastRunTxt = lastRun
    ? lastRun.toLocaleString(locale === "en" ? "en-US" : "tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : (locale === "en" ? "not yet" : "henüz çalıştırılmadı");

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/[0.04] p-5" data-testid="daily-briefing">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 text-primary grid place-items-center flex-shrink-0">
          <Robot size={20} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="acas-eyebrow text-primary/80">{t("Pazar Brifingi", "Market Brief")}</div>
          <h2 className="text-lg font-bold mt-1">
            {totals.autonomous_runs > 0
              ? t(
                  `Son 24 saatte ${totals.new_leads} yeni fırsat, ${totals.high_priority} yüksek öncelikli.`,
                  `In the last 24h I surfaced ${totals.new_leads} new opportunities · ${totals.high_priority} high-priority.`
                )
              : t(
                  "Otonom keşif yakında çalışacak. Bekliyorum.",
                  "Autonomous discovery will run soon. Standing by."
                )}
          </h2>
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span data-testid="briefing-new-leads">
              <strong className="text-foreground">{totals.new_leads}</strong> {t("yeni fırsat", "new opportunities")}
            </span>
            <span data-testid="briefing-high-priority">
              <strong className="text-foreground">{totals.high_priority}</strong> {t("yüksek öncelik", "high priority")}
            </span>
            <span data-testid="briefing-contact-available">
              <strong className="text-foreground">{totals.contact_available}</strong> {t("iletişimi mevcut", "with contact info")}
            </span>
            <span title={t("Son otonom çalıştırma", "Last autonomous run")}>
              {t("Son çalıştırma:", "Last run:")} {lastRunTxt}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Today's Opportunities — collapsible. Reads the last autonomous session's
// results from /agent/latest.
// ---------------------------------------------------------------------------
function TodaysOpportunitiesCard({ locale, t, nav }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    api.get("/agent/latest").then((r) => setSession(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const results = (session?.results || []).slice(0, 5);

  return (
    <div className="rounded-lg border border-border/60 bg-card" data-testid="todays-opportunities">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-5 text-left"
        data-testid="opportunities-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-primary/10 text-primary grid place-items-center">
            <Sparkle size={16} weight="fill" />
          </div>
          <div>
            <div className="text-sm font-bold tracking-tight">
              {t("Bugünün Fırsatları", "Today's Opportunities")}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {loading
                ? t("Yükleniyor…", "Loading…")
                : results.length > 0
                  ? t(`${results.length} önerilen fırsat`, `${results.length} recommended opportunities`)
                  : t("Bugün için otonom fırsat yok — manuel arama başlatabilirsiniz.", "No autonomous opportunities today — try a manual search.")}
            </div>
          </div>
        </div>
        {open ? <CaretDown size={16} className="text-muted-foreground" /> : <CaretRight size={16} className="text-muted-foreground" />}
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-border/60 pt-4 space-y-2" data-testid="opportunities-list">
          {loading && (
            <div className="h-24 animate-pulse rounded-md bg-muted/20" />
          )}
          {!loading && results.length === 0 && (
            <div className="rounded-md border border-dashed border-border/50 p-5 text-center">
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {t(
                  "Otonom AI çalışanınız bir sonraki döngüde tekrar çalışacak. İsterseniz şimdi kendiniz de bir arama başlatabilirsiniz.",
                  "Your autonomous AI employee will run again in the next cycle. Or start a manual search right now."
                )}
              </p>
              <Button
                size="sm"
                className="mt-3 gap-1.5"
                onClick={() => nav("/arama")}
                data-testid="opportunities-empty-cta"
              >
                <MagnifyingGlass size={13} />
                {t("Manuel Arama Başlat", "Start Manual Search")}
              </Button>
            </div>
          )}
          {!loading && results.map((lead) => (
            <OpportunityRow key={lead.id} lead={lead} locale={locale} onOpen={() => nav(`/musteriler/${lead.id}`)} />
          ))}
          {!loading && results.length > 0 && (
            <div className="flex justify-end pt-1">
              <a
                href="/gecmis"
                data-testid="opportunities-see-all"
                className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
              >
                {t("Tüm geçmişi gör", "See full history")}
                <ArrowUpRight size={12} />
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const SOURCE_META = {
  "Google Maps": { icon: MapPin, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-500/10 border-blue-500/25" },
  "Instagram":   { icon: InstagramLogo, color: "text-pink-600 dark:text-pink-400", bg: "bg-pink-500/10 border-pink-500/25" },
  "LinkedIn":    { icon: LinkedinLogo, color: "text-sky-700 dark:text-sky-400", bg: "bg-sky-500/10 border-sky-500/25" },
  "Website":     { icon: Globe, color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/25" },
};

function OpportunityRow({ lead, locale, onOpen }) {
  const t = (tr, en) => (locale === "en" ? en : tr);
  const meta = SOURCE_META[lead.source] || { icon: Buildings, color: "text-muted-foreground", bg: "bg-muted/40 border-border/60" };
  const SrcIcon = meta.icon;
  return (
    <button
      data-testid={`opportunity-row-${lead.id}`}
      onClick={onOpen}
      className="w-full text-left rounded-md border border-border/60 bg-card p-3 hover:border-primary/40 hover:bg-primary/[0.03] transition flex items-start gap-3"
    >
      <div className={`w-9 h-9 rounded-md border grid place-items-center flex-shrink-0 ${meta.bg} ${meta.color}`}>
        <SrcIcon size={15} weight="duotone" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-sm truncate">{lead.company_name}</div>
          {lead.priority === "high" && (
            <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded border border-primary/30 bg-primary/10 text-primary font-semibold">
              {t("Yüksek", "High")}
            </span>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 truncate">
          {lead.industry && <span>{lead.industry}</span>}
          {lead.location && <><span>·</span><span className="inline-flex items-center gap-0.5"><MapPin size={10} />{lead.location}</span></>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {typeof lead.match_score === "number" && (
          <span className="mono text-xs font-semibold text-primary">{lead.match_score}</span>
        )}
        <ArrowUpRight size={13} className="text-muted-foreground" />
      </div>
    </button>
  );
}


// ---------------------------------------------------------------------------
// Strategic Snapshot — the top 2 recommendations from the latest daily
// briefing. Full page lives at /ai-analiz.
// ---------------------------------------------------------------------------
function StrategicSnapshotCard({ locale, t, nav }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/ai-insights").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="rounded-lg border border-border/60 bg-card p-5 h-[120px] animate-pulse" />;
  }
  if (!data) return null;

  const recs = (data.recommendations || []).slice(0, 3);
  const hasContent = !!data.summary || recs.length > 0;

  if (!hasContent) return null;

  return (
    <div className="rounded-lg border border-border/60 bg-card p-5" data-testid="strategic-snapshot">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-primary/10 text-primary grid place-items-center">
            <Lightbulb size={15} weight="duotone" />
          </div>
          <div>
            <div className="text-sm font-bold tracking-tight">{t("Stratejik Not", "Strategic Note")}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("Bu haftanın sektörel gelişmelerinden", "From this week's industry signals")}
            </div>
          </div>
        </div>
        <a
          href="/ai-analiz"
          data-testid="strategic-open-full"
          className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
        >
          {t("Tümünü aç", "Open full")}
          <ArrowUpRight size={11} />
        </a>
      </div>

      {data.summary && (
        <p className="text-sm leading-relaxed text-foreground/90" data-testid="strategic-summary">
          {data.summary}
        </p>
      )}
      {recs.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="strategic-recommendations">
          {recs.map((r, i) => (
            <li key={i} className="text-sm text-foreground/90 flex gap-2">
              <span className="text-primary mt-0.5">→</span>
              <span>{typeof r === "string" ? r : (r.text || r.action || JSON.stringify(r))}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Quick actions row.
// ---------------------------------------------------------------------------
function QuickActionsRow({ t, nav }) {
  const items = [
    {
      label: t("Manuel Arama", "Manual Search"),
      hint:  t("Yeni bir fırsat listesi oluştur", "Create a new opportunity list"),
      onClick: () => nav("/arama"),
      testid: "qa-search",
      icon: <MagnifyingGlass size={16} weight="fill" />,
    },
    {
      label: t("Geçmiş Aramalar", "Past Searches"),
      hint:  t("Önceki sonuçları ve mesajları gör", "Review previous results & drafts"),
      onClick: () => nav("/gecmis"),
      testid: "qa-history",
      icon: <ClockCounterClockwise size={16} weight="fill" />,
    },
    {
      label: t("AI Öngörüleri", "AI Insights"),
      hint:  t("Sektörel gelişmeler ve tavsiyeler", "Industry developments & advice"),
      onClick: () => nav("/ai-analiz"),
      testid: "qa-ai",
      icon: <Sparkle size={16} weight="fill" />,
    },
    {
      label: t("İşletme Profili", "Business Profile"),
      hint:  t("Daha iyi eşleşmeler için güncelle", "Update for better matches"),
      onClick: () => nav("/isletme"),
      testid: "qa-profile",
      icon: <Buildings size={16} weight="fill" />,
    },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="quick-actions">
      {items.map((it) => (
        <button
          key={it.testid}
          type="button"
          onClick={it.onClick}
          data-testid={it.testid}
          className="text-left rounded-lg border border-border/60 bg-card p-4 hover:border-primary/50 hover:bg-primary/[0.03] transition h-full flex items-start gap-3"
        >
          <div className="w-8 h-8 rounded-md bg-primary/10 text-primary grid place-items-center flex-shrink-0">
            {it.icon}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate">{it.label}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{it.hint}</div>
          </div>
        </button>
      ))}
    </div>
  );
}
